import { CellValueChangedEvent } from '@ag-grid-community/core';
import { inject, Injectable } from '@angular/core';
import { createAction, emptyProps, Store } from '@ngrx/store';
import { of, switchMap } from 'rxjs';
import { ApiUpdateValueRequest } from '../../../api/core/models';
import { ChangeReason } from '../../../api/core/models/api/change-reason';
import { InventoriesVariablesValuesResponse } from '../../../api/core/models/api/inventories-variables-values-response';
import { UpdateValuesRequest } from '../../../api/core/models/api/update-values-request';
import { InventoryService } from '../../../api/core/services';
import { EditorInventoryMultiYearLayoutComponent } from '../../../feature/editor-inventory-multiyear/editor-inventory-multiyear-layout.component';
import { parseSourceString } from '../../../feature/editor-inventory-multiyear/services/value-setters/parse-source-string';
import { MultiyearLeafNodeRow } from '../../../feature/editor-inventory-multiyear/types/leaf-row';
import { InventoryUpdate } from '../../../services/store/inventories/inventory-save-manager.service';
import { selectRouteBoardId } from '../../router/router.selectors';

type CellValueChanged = Parameters<EditorInventoryMultiYearLayoutComponent['onCellValueChanged']>[0];

export const clearVariablesAction = createAction('[Multi Year Service] Clear variables', emptyProps);

/**
 * Temporary service for updating values over multiple inventories
 * where we don't support multiyear editing.
 */
@Injectable({ providedIn: 'root' })
export class MultiyearUpdateValuesService {
  private _inventoryService = inject(InventoryService);
  private _store = inject(Store);

  private _cachedValues: InventoriesVariablesValuesResponse | undefined;

  constructor() {
    this._store.select(selectRouteBoardId).subscribe(() => {
      this._cachedValues = undefined;
    });
  }

  clearState() {
    this._cachedValues = undefined;
    this._store.dispatch(clearVariablesAction());
  }

  isGrowthFactorUpdate(
    growthFactor: EditorInventoryMultiYearLayoutComponent['growthFactor'],
    event: CellValueChangedEvent<MultiyearLeafNodeRow, number>
  ): boolean {
    if (!growthFactor) {
      return false;
    }
    const growthFactorId = growthFactor?.rows?.[0]?.id;
    const eventNodeDataId = event.node.data?.id;
    if (growthFactorId === undefined) {
      console.error('Growth factor set, but no growth factor id found: ', growthFactor);
      return false;
    }
    if (eventNodeDataId === undefined) {
      console.error('Growth factor set, but no node data id found in event: ', event);
      return false;
    }
    return growthFactorId === eventNodeDataId;
  }

  getGrowthFactorUpdates({
    inventories,
    update: { newValue, oldValue, origin, variableId },
  }: {
    inventories: EditorInventoryMultiYearLayoutComponent['_inventories'];
    update: {
      newValue: CellValueChanged['newValue'];
      oldValue: CellValueChanged['oldValue'];
      variableId: NonNullable<NonNullable<CellValueChanged['node']['data']>['options']>['variableId'];
      origin: ReturnType<typeof parseSourceString>;
    };
  }): InventoryUpdate[] {
    const updates = [...inventories.values()]
      .flatMap((i) => i.variables)
      .filter((v) => v.name === variableId)
      .map((v) => ({
        valueId: v.valueId!,
        value: newValue!,
        oldValue: oldValue ?? 0,
        inventoryId: v.inventoryId,
        variableId: v.name,
        origin,
      }));
    if (updates.length === 0) {
      console.error('No updates found for growth factor with variableId: ', variableId);
      return [];
    }
    if (updates.some((u) => !u.valueId || u.value === undefined || u.value === null)) {
      console.error('Required valueId or value is missing for growth factor update with variableId: ', variableId);
      return [];
    }
    return updates;
  }

  updateValues({
    boardId,
    variableDefinitionId,
    value,
    isMonetaryVariable,
    valueMetaData,
  }: {
    boardId: string;
    variableDefinitionId: string;
    value: number;
    isMonetaryVariable: boolean;
    valueMetaData?: ChangeReason;
  }) {
    const request$ = this._cachedValues
      ? of(this._cachedValues)
      : this._inventoryService.listInventoriesVariablesValues({ boardId });
    return request$.pipe(
      switchMap((res) => {
        this._cachedValues = res;
        const valuesToUpdate = isMonetaryVariable ? res.monetaryVariablesValues : res.variablesValues;
        const values = valuesToUpdate[variableDefinitionId]?.reduce(
          (acc, val) => {
            acc[val.id] = { value, ...(valueMetaData ? { changeReason: valueMetaData } : {}) };
            return acc;
          },
          <{ [key: string]: ApiUpdateValueRequest }>{}
        );
        const body = (isMonetaryVariable ? { monetaryValues: values } : { values }) as unknown as UpdateValuesRequest;
        return this._inventoryService.updateValues({ boardId, body });
      })
    );
  }
}
